/**
 * Software solutions shown on the home page and at /solutions. Each item with a slug has its own
 * page at /solutions/<slug>. The site reads them from website_content `solutions.items`; these
 * are the defaults until that list is saved from the editor.
 *
 * LimpioMail's page is the "Application home page" registered in Google Cloud for Gmail OAuth
 * verification, so it must keep the app name, what the app does with Gmail data, and links to
 * the privacy policy and terms on this domain. The Worker also serves this text as static HTML
 * for crawlers (cloudflare/prerender.js).
 */
import type { LocalizedText } from "./websiteDefaults";

export const SOLUTIONS_PATH = "/solutions";

export const SOLUTION_CATEGORIES = ["apps", "ai", "software", "integrations"] as const;
export type SolutionCategory = (typeof SOLUTION_CATEGORIES)[number];

export type SolutionFeature = {
  title: LocalizedText | string;
  description: LocalizedText | string;
  /** focus | clean | shield | sparkles | code | plug | bell | lock */
  icon?: string;
};

export type SolutionItem = {
  slug?: string;
  name: string;
  /** "iPhone app · Productivity" */
  kind?: LocalizedText | string;
  category?: SolutionCategory | string;
  status?: "available" | "soon";
  /** Short text for cards. */
  summary?: LocalizedText | string;
  /** Page headline, e.g. "LimpioMail: your email, in order". */
  headline?: LocalizedText | string;
  /** Lead paragraph on the solution page. */
  description?: LocalizedText | string;
  icon?: string;
  /** Screenshot for cards and the home section. */
  image?: string;
  screenshots?: string[];
  tags?: Array<LocalizedText | string>;
  /** "Free · iOS 18 or later · English and Spanish" */
  platform?: LocalizedText | string;
  appStoreUrl?: string;
  websiteUrl?: string;
  privacyUrl?: string;
  termsUrl?: string;
  supportUrl?: string;
  features?: SolutionFeature[];
  dataTitle?: LocalizedText | string;
  dataPoints?: Array<LocalizedText | string>;
  /** Shown as the large card in the home section. */
  featured?: boolean;
  hidden?: boolean;
};

const L = (en: string, es: string): LocalizedText => ({ en, es });
const S = (file: string) => `/site/solutions/${file}`;

export const LIMPIOMAIL: SolutionItem = {
  slug: "limpiomail",
  name: "LimpioMail",
  kind: L("iPhone app · Productivity", "App iPhone · Productividad"),
  category: "apps",
  status: "available",
  featured: true,
  summary: L(
    "Email management for Gmail and iCloud: focus first, one-tap cleanup, phishing warnings and full on-device privacy.",
    "Gestión de correo para Gmail e iCloud: enfoque, limpieza con un toque, avisos de phishing y privacidad total en el dispositivo."
  ),
  headline: L("LimpioMail: your email, in order", "LimpioMail: tu correo, en orden"),
  description: L(
    "LimpioMail separates what matters from what doesn't, without making you read it all. It works with Gmail and iCloud Mail, and your email never leaves your iPhone.",
    "LimpioMail separa lo importante de lo que no, sin que tengas que leerlo todo. Funciona con Gmail y iCloud Mail, y tu correo nunca sale de tu iPhone."
  ),
  icon: S("limpiomail-icon.png"),
  image: S("limpiomail-inbox.jpg"),
  screenshots: [S("limpiomail-welcome.jpg"), S("limpiomail-inbox.jpg"), S("limpiomail-phishing.jpg")],
  tags: [L("iPhone app", "App iPhone"), "Gmail · iCloud", L("Free", "Gratis")],
  platform: L("Free · iOS 18 or later · English and Spanish", "Gratis · iOS 18 o posterior · Español e inglés"),
  appStoreUrl: "https://apps.apple.com/us/app/limpiomail/id6823013209",
  privacyUrl: "/app/limpiomail/privacy",
  termsUrl: "/app/limpiomail/terms",
  supportUrl: "/contact",
  features: [
    {
      icon: "focus",
      title: L("Focus first", "Primero lo importante"),
      description: L(
        "The inbox opens on Focus: people, threads you replied to, security alerts and payments due. Tabs for People, Notifications, Promotions and Transactions keep the rest in its place.",
        "La bandeja abre en Enfoque: personas, hilos que respondiste, alertas de seguridad y pagos pendientes. Las pestañas Personas, Notificaciones, Promociones y Transacciones ponen el resto en su lugar."
      ),
    },
    {
      icon: "clean",
      title: L("One tap to clean", "Limpia con un toque"),
      description: L(
        "Clean marks notifications and promotions as read and archives them. You see what it will do first, and you can undo it. It never touches email from people or senders you mark as important.",
        "Limpiar marca como leídas y archiva las notificaciones y promociones. Ves antes qué va a hacer y puedes deshacerlo. Nunca toca correos de personas ni de remitentes que marques como importantes."
      ),
    },
    {
      icon: "shield",
      title: L("Safer email", "Correo más seguro"),
      description: L(
        "Phishing warnings with the reasons why, a locked-down viewer with no scripts, trackers or remote images, and every link shows its real address before it opens.",
        "Avisos de phishing con el motivo, un visor sin scripts, rastreadores ni imágenes remotas, y cada enlace muestra su dirección real antes de abrirse."
      ),
    },
    {
      icon: "bell",
      title: L("Less noise", "Menos ruido"),
      description: L(
        "Noise radar shows the senders that fill your inbox most, with one-tap unsubscribe. Verification codes on top, shipping updates grouped, and reminders to bring an email back later.",
        "El radar de ruido muestra los remitentes que más llenan tu bandeja, con baja en un toque. Códigos de verificación arriba, envíos agrupados y recordatorios para volver a un correo más tarde."
      ),
    },
    {
      icon: "sparkles",
      title: L("Private daily summary", "Resumen diario privado"),
      description: L(
        "A summary of what's important, written on the device by Apple Intelligence on supported iPhones.",
        "Un resumen de lo importante, escrito en el propio iPhone por Apple Intelligence en los modelos compatibles."
      ),
    },
    {
      icon: "lock",
      title: L("Built for your iPhone", "Hecha para tu iPhone"),
      description: L(
        "Widgets, Control Center, Siri and Shortcuts, Spotlight, Focus filters, an unread badge and a Face ID lock.",
        "Widgets, Centro de control, Siri y Atajos, Spotlight, filtros de Concentración, contador de no leídos y bloqueo con Face ID."
      ),
    },
  ],
  dataTitle: L("How LimpioMail uses your Google data", "Cómo usa LimpioMail tus datos de Google"),
  dataPoints: [
    L(
      "With your permission, LimpioMail connects to Gmail through Google's official API with the gmail.modify scope, to read and display your messages, archive them, mark them as read or move them to Spam.",
      "Con tu permiso, LimpioMail se conecta a Gmail mediante la API oficial de Google con el permiso gmail.modify, para leer y mostrar tus mensajes, archivarlos, marcarlos como leídos o moverlos a Spam."
    ),
    L(
      "It does not send email on your behalf and does not permanently delete messages.",
      "No envía correo en tu nombre ni borra mensajes de forma permanente."
    ),
    L(
      "Everything is processed on your iPhone. ON45D Studio has no servers that receive your email; sign-ins are stored encrypted in the iOS Keychain, and the app only talks to Google and Apple.",
      "Todo se procesa en tu iPhone. ON45D Studio no tiene servidores que reciban tu correo; los accesos se guardan cifrados en el Llavero de iOS y la app solo se comunica con Google y Apple."
    ),
    L(
      "It does not sell or share your data, has no ads or analytics, and does not use your email to train AI models.",
      "No vende ni comparte tus datos, no tiene publicidad ni analíticas, y no usa tu correo para entrenar modelos de IA."
    ),
    L(
      "LimpioMail's use of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements. You can revoke access at any time at myaccount.google.com/permissions.",
      "El uso que hace LimpioMail de la información recibida de las API de Google cumple la Google API Services User Data Policy, incluidos los requisitos de Limited Use. Puedes revocar el acceso en cualquier momento en myaccount.google.com/permissions."
    ),
  ],
};

export const SOLUTION_DEFAULTS: SolutionItem[] = [LIMPIOMAIL];

/** Solutions that are not hidden, in the order the editor lists them. */
export function visibleSolutions(items: unknown): SolutionItem[] {
  const list = Array.isArray(items) && items.length ? (items as SolutionItem[]) : SOLUTION_DEFAULTS;
  return list.filter((item) => item && item.name && !item.hidden);
}

export function solutionHref(item: SolutionItem): string {
  if (item.slug) return `${SOLUTIONS_PATH}/${item.slug}`;
  return item.websiteUrl || item.appStoreUrl || SOLUTIONS_PATH;
}
